// CalendarWidget.js — floating calendar icon paired with the WhatsApp
// widget. Opens a month view; clicking a date shows that day's events
// (application deadlines, holidays, intake dates — entered by staff in
// Website Admin, see ve_backend blog.CalendarEvent).
import React, { useState, useEffect, useRef, useCallback } from 'react';
import { CalendarDays, X, ChevronLeft, ChevronRight } from 'lucide-react';
import useFocusTrap from '../hooks/useFocusTrap';
import { getCalendarEvents } from '../services/api';
import './CalendarWidget.css';

const WEEKDAYS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

const EVENT_TYPE_LABEL = {
  deadline: 'Deadline',
  intake: 'Intake',
  holiday: 'Holiday',
  exam: 'Exam',
  other: 'Event',
};

function toDateKey(year, month, day) {
  return `${year}-${String(month + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
}

function monthLabel(year, month) {
  return new Date(year, month, 1).toLocaleDateString('en-US', { month: 'long', year: 'numeric' });
}

/** Every day this month/year an event's [date, end_date] range touches, mapped to that day's events. */
function buildDayMap(events, year, month) {
  const map = {};
  const monthStart = new Date(year, month, 1);
  const monthEnd = new Date(year, month + 1, 0);

  events.forEach((event) => {
    const start = new Date(`${event.date}T00:00:00`);
    const end = event.end_date ? new Date(`${event.end_date}T00:00:00`) : start;
    const rangeStart = start < monthStart ? monthStart : start;
    const rangeEnd = end > monthEnd ? monthEnd : end;
    for (let d = new Date(rangeStart); d <= rangeEnd; d.setDate(d.getDate() + 1)) {
      const key = toDateKey(d.getFullYear(), d.getMonth(), d.getDate());
      (map[key] = map[key] || []).push(event);
    }
  });
  return map;
}

const CalendarWidget = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [viewDate, setViewDate] = useState(() => new Date());
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [selectedKey, setSelectedKey] = useState(null);

  const panelRef = useRef(null);
  const close = useCallback(() => setIsOpen(false), []);
  useFocusTrap(panelRef, isOpen, close);

  const year = viewDate.getFullYear();
  const month = viewDate.getMonth();

  const loadMonth = useCallback((signal) => {
    setLoading(true);
    setError(null);
    const monthParam = `${year}-${String(month + 1).padStart(2, '0')}`;
    getCalendarEvents({ month: monthParam }, { signal })
      .then((data) => setEvents(Array.isArray(data) ? data : (data.results || [])))
      .catch((err) => {
        if (err.name === 'AbortError') return;
        setError('Could not load the calendar. Please try again.');
      })
      .finally(() => setLoading(false));
  }, [year, month]);

  useEffect(() => {
    if (!isOpen) return undefined;
    const controller = new AbortController();
    loadMonth(controller.signal);
    return () => controller.abort();
  }, [isOpen, loadMonth]);

  const dayMap = buildDayMap(events, year, month);
  const todayKey = toDateKey(new Date().getFullYear(), new Date().getMonth(), new Date().getDate());
  const selectedEvents = selectedKey ? (dayMap[selectedKey] || []) : [];

  const firstWeekday = new Date(year, month, 1).getDay();
  const daysInMonth = new Date(year, month + 1, 0).getDate();
  const cells = [
    ...Array.from({ length: firstWeekday }, () => null),
    ...Array.from({ length: daysInMonth }, (_, i) => i + 1),
  ];

  const goToMonth = (delta) => {
    setSelectedKey(null);
    setViewDate(new Date(year, month + delta, 1));
  };

  return (
    <>
      <button
        type="button"
        className="calendar-widget-toggle"
        onClick={() => setIsOpen(true)}
        aria-label="Open important dates calendar"
        aria-haspopup="dialog"
      >
        <CalendarDays size={26} aria-hidden="true" />
      </button>

      {isOpen && (
        <div className="calendar-widget-overlay" onClick={close}>
          <div
            className="calendar-widget-panel"
            role="dialog"
            aria-modal="true"
            aria-label="Important dates calendar"
            ref={panelRef}
            tabIndex={-1}
            onClick={(e) => e.stopPropagation()}
          >
            <div className="calendar-widget-header">
              <h2 className="calendar-widget-title">Important Dates</h2>
              <button type="button" className="calendar-widget-close" onClick={close} aria-label="Close calendar">
                <X size={20} aria-hidden="true" />
              </button>
            </div>

            <div className="calendar-nav">
              <button type="button" onClick={() => goToMonth(-1)} aria-label="Previous month" className="calendar-nav-btn">
                <ChevronLeft size={18} aria-hidden="true" />
              </button>
              <span className="calendar-month-label">{monthLabel(year, month)}</span>
              <button type="button" onClick={() => goToMonth(1)} aria-label="Next month" className="calendar-nav-btn">
                <ChevronRight size={18} aria-hidden="true" />
              </button>
            </div>

            {error ? (
              <div className="calendar-error">
                <p>{error}</p>
                <button type="button" onClick={() => loadMonth()} className="calendar-retry">Try Again</button>
              </div>
            ) : (
              <>
                <div className="calendar-weekdays">
                  {WEEKDAYS.map((w) => <span key={w}>{w}</span>)}
                </div>
                <div className={`calendar-grid${loading ? ' calendar-grid--loading' : ''}`}>
                  {cells.map((day, i) => {
                    if (day === null) return <span key={`pad-${i}`} className="calendar-cell calendar-cell--empty" />;
                    const key = toDateKey(year, month, day);
                    const hasEvents = Boolean(dayMap[key]?.length);
                    const isToday = key === todayKey;
                    const isSelected = key === selectedKey;
                    return (
                      <button
                        type="button"
                        key={key}
                        className={`calendar-cell${isToday ? ' calendar-cell--today' : ''}${isSelected ? ' calendar-cell--selected' : ''}${hasEvents ? ' calendar-cell--has-events' : ''}`}
                        onClick={() => setSelectedKey(isSelected ? null : key)}
                        aria-pressed={isSelected}
                        aria-label={`${monthLabel(year, month)} ${day}${hasEvents ? `, ${dayMap[key].length} event${dayMap[key].length === 1 ? '' : 's'}` : ''}`}
                      >
                        {day}
                        {hasEvents && <span className="calendar-dot" aria-hidden="true" />}
                      </button>
                    );
                  })}
                </div>

                <div className="calendar-day-events">
                  {!selectedKey ? (
                    <p className="calendar-hint">Click a highlighted date to see what's happening.</p>
                  ) : selectedEvents.length === 0 ? (
                    <p className="calendar-hint">No events on this date.</p>
                  ) : (
                    <ul className="calendar-event-list">
                      {selectedEvents.map((event) => (
                        <li key={event.id} className="calendar-event-item">
                          <span className={`calendar-event-type calendar-event-type--${event.event_type}`}>
                            {EVENT_TYPE_LABEL[event.event_type] || 'Event'}
                          </span>
                          <span className="calendar-event-title">{event.title}</span>
                          {event.description && <p className="calendar-event-description">{event.description}</p>}
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
              </>
            )}
          </div>
        </div>
      )}
    </>
  );
};

export default CalendarWidget;
